import type { AIRecommendation, SupplierAdvice } from '../types'

export interface SupplierCandidate {
  supplier: string
  price: number
  deliveryDays: number
  reliability?: number
  historyWins?: number
}

/**
 * SupplierAdvisor — scores suppliers by price, delivery, reliability and history.
 */
export class SupplierAdvisor {
  advise(product: string, candidates: SupplierCandidate[]): SupplierAdvice {
    if (candidates.length === 0) {
      return {
        supplier: '—',
        score: 0,
        reason: `Nenhum fornecedor candidato para ${product}`,
        priceSignal: 0,
        deliverySignal: 0,
        reliabilitySignal: 0,
        recommendations: [],
      }
    }

    const prices = candidates.map((c) => c.price)
    const deliveries = candidates.map((c) => c.deliveryDays)
    const reliabilities = candidates.map((c) => c.reliability ?? 70)
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const minDelivery = Math.min(...deliveries)
    const maxDelivery = Math.max(...deliveries)
    const minRel = Math.min(...reliabilities)
    const maxRel = Math.max(...reliabilities)

    const scored = candidates.map((c) => {
      const priceScore = invertNorm(c.price, minPrice, maxPrice)
      const deliveryScore = invertNorm(c.deliveryDays, minDelivery, maxDelivery)
      const reliabilityScore = norm(c.reliability ?? 70, minRel, maxRel)
      const historyBoost = Math.min(0.15, (c.historyWins ?? 0) * 0.02)
      const score = round2((priceScore * 0.45 + deliveryScore * 0.25 + reliabilityScore * 0.3 + historyBoost) * 100)
      return { candidate: c, score, priceScore, deliveryScore, reliabilityScore }
    })

    scored.sort((a, b) => b.score - a.score)
    const best = scored[0]!

    const recommendations: AIRecommendation[] = [
      {
        id: crypto.randomUUID(),
        kind: 'supplier',
        title: `Fornecedor recomendado: ${best.candidate.supplier}`,
        suggestion: `Priorizar ${best.candidate.supplier} para ${product}`,
        reason: `Melhor equilíbrio entre preço (${best.candidate.price}), prazo (${best.candidate.deliveryDays}d) e confiabilidade (${best.candidate.reliability ?? 70})`,
        confidence: Math.min(0.95, best.score / 100),
        impact: 'Reduz risco comercial e custo total esperado',
        requiresConfirmation: true,
      },
    ]

    if (scored[1] && best.score - scored[1].score < 5) {
      recommendations.push({
        id: crypto.randomUUID(),
        kind: 'supplier',
        title: 'Alternativa próxima',
        suggestion: `Avaliar também ${scored[1].candidate.supplier}`,
        reason: `Diferença de score de apenas ${(best.score - scored[1].score).toFixed(1)} pontos`,
        confidence: 0.6,
        requiresConfirmation: true,
      })
    }

    return {
      supplier: best.candidate.supplier,
      score: best.score,
      reason: recommendations[0]!.reason,
      priceSignal: round2(best.priceScore * 100),
      deliverySignal: round2(best.deliveryScore * 100),
      reliabilitySignal: round2(best.reliabilityScore * 100),
      recommendations,
    }
  }
}

function norm(value: number, min: number, max: number): number {
  if (max === min) return 1
  return (value - min) / (max - min)
}

function invertNorm(value: number, min: number, max: number): number {
  return 1 - norm(value, min, max)
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const supplierAdvisor = new SupplierAdvisor()
